import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { MessageSquare, Bug, Lightbulb, BookOpen, HelpCircle, Send, Loader2, Clock } from "lucide-react";

const CATEGORIES = [
  { id: "content",    label: "Content",    icon: BookOpen,   desc: "Something is wrong or missing in a topic, question, or analogy." },
  { id: "bug",        label: "Bug",        icon: Bug,        desc: "Something in the app is broken or acting weird." },
  { id: "suggestion", label: "Suggestion", icon: Lightbulb,  desc: "An idea to make PictureMed better." },
  { id: "other",      label: "Other",      icon: HelpCircle, desc: "Anything else you'd like to share." },
];

function CategoryColor(id) {
  return {
    content: "#4f7942",
    bug: "#c95e5e",
    suggestion: "#c98a3b",
    other: "#6a7570",
  }[id] || "var(--primary)";
}

export default function Feedback() {
  const [category, setCategory] = useState("content");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [history, setHistory] = useState([]);

  const loadHistory = () => {
    api.get("/feedback/mine")
      .then((r) => setHistory(r.data.items || []))
      .catch(() => setHistory([]));
  };
  useEffect(() => { loadHistory(); }, []);

  const submit = async () => {
    const clean = message.trim();
    if (clean.length < 3) {
      toast.error("Please share a bit more detail.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/feedback", {
        category,
        message: clean,
        page: window.location.pathname,
      });
      toast.success("Thanks! Your feedback has been received.");
      setMessage("");
      loadHistory();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something went wrong. Try again?";
      toast.error(typeof detail === "string" ? detail : "Submit failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const catInfo = CATEGORIES.find((c) => c.id === category);
  const canSubmit = message.trim().length >= 3 && !submitting;

  return (
    <div className="max-w-3xl mx-auto" data-testid="feedback-page">
      <div className="flex items-center gap-3 mb-2">
        <div
          className="w-10 h-10 rounded-2xl flex items-center justify-center"
          style={{ background: "var(--primary)" }}
        >
          <MessageSquare size={18} color="white" />
        </div>
        <h1 className="font-display text-4xl font-bold">Feedback</h1>
      </div>
      <p className="text-lg mb-8" style={{ color: "var(--text-secondary)" }}>
        Help us shape PictureMed. Tell us what&apos;s working, what&apos;s confusing, or what you wish existed.
      </p>

      {/* Category picker */}
      <div className="mb-4" data-testid="feedback-category-group">
        <div className="text-xs uppercase tracking-wider mb-2" style={{ color: "var(--text-muted)" }}>
          Category
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2">
          {CATEGORIES.map((c) => {
            const Icon = c.icon;
            const active = category === c.id;
            return (
              <button
                key={c.id}
                onClick={() => setCategory(c.id)}
                data-testid={`feedback-category-${c.id}`}
                className="pm-card p-3 text-left transition-transform hover:-translate-y-0.5"
                style={{
                  border: `1.5px solid ${active ? CategoryColor(c.id) : "var(--border)"}`,
                  boxShadow: active ? `0 0 0 2px ${CategoryColor(c.id)}22` : "none",
                  background: active ? "var(--paper)" : "var(--paper)",
                }}
                aria-pressed={active}
              >
                <div className="flex items-center gap-2 mb-1">
                  <Icon size={16} style={{ color: CategoryColor(c.id) }} />
                  <span className="font-display font-bold text-sm">{c.label}</span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <p className="text-xs mb-4" style={{ color: "var(--text-muted)" }} data-testid="feedback-category-hint">
        {catInfo?.desc}
      </p>

      <div className="pm-card p-4 mb-4">
        <label htmlFor="fb-message" className="text-xs uppercase tracking-wider mb-2 block" style={{ color: "var(--text-muted)" }}>
          Your feedback
        </label>
        <textarea
          id="fb-message"
          data-testid="feedback-message"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder={
            category === "content"
              ? "Which topic or question? What was wrong or unclear?"
              : category === "bug"
              ? "What did you click, what did you expect, and what happened instead?"
              : category === "suggestion"
              ? "Describe the idea and who it would help."
              : "Tell us anything on your mind."
          }
          rows={6}
          maxLength={4000}
          className="w-full p-3 rounded-xl text-sm resize-y focus:outline-none focus:ring-2"
          style={{
            background: "var(--bg)",
            border: "1px solid var(--border)",
            color: "var(--text-primary)",
            fontFamily: "inherit",
            outlineColor: "var(--primary)",
          }}
        />
        <div className="flex items-center justify-between mt-2 text-xs" style={{ color: "var(--text-muted)" }}>
          <span data-testid="feedback-char-count">{message.length} / 4000</span>
          <span>You&apos;re posting from <code style={{ color: "var(--text-secondary)" }}>{window.location.pathname}</code></span>
        </div>
      </div>

      <div className="flex justify-end mb-10">
        <button
          onClick={submit}
          disabled={!canSubmit}
          className="pm-btn-primary inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="feedback-submit-btn"
        >
          {submitting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
          {submitting ? "Sending…" : "Send feedback"}
        </button>
      </div>

      {/* Past submissions */}
      <div data-testid="feedback-history">
        <h2 className="font-display font-bold text-xl mb-3">Your past feedback</h2>
        {history.length === 0 ? (
          <div className="pm-card p-6 text-center text-sm" style={{ color: "var(--text-muted)" }} data-testid="feedback-history-empty">
            You haven&apos;t sent any feedback yet — the field above is a good place to start.
          </div>
        ) : (
          <ul className="space-y-2">
            {history.map((f) => (
              <li key={f.id} className="pm-card p-4" data-testid={`feedback-item-${f.id}`}>
                <div className="flex items-center justify-between mb-1">
                  <span
                    className="pm-badge text-xs"
                    style={{ background: `${CategoryColor(f.category)}22`, color: CategoryColor(f.category) }}
                  >
                    {f.category}
                  </span>
                  <span className="text-xs inline-flex items-center gap-1" style={{ color: "var(--text-muted)" }}>
                    <Clock size={12} /> {new Date(f.created_at).toLocaleString()}
                  </span>
                </div>
                <p className="text-sm whitespace-pre-wrap" style={{ color: "var(--text-primary)" }}>{f.message}</p>
                {f.page && (
                  <div className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>
                    on <code>{f.page}</code>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
